/**
 * LINE 文字訊息 API：仍以半形 $ + emojis[].index 傳送。
 * 編輯器改以 {{$1}}、{{$2}} 等可見佔位對應 JSON 的 slot，可隨意剪貼移動；
 * 存檔時換算為 $ 與正確 index。
 */

/** 訊息內表情佔位（編號須與 emojis 陣列的 slot 一致） */
export const LINE_EMOJI_BRACKET_RE = /\{\{\$(\d+)\}\}/g;

/** 字串結尾可再補成 {{$n}} 的前綴（勿刪除，以便使用者正在輸入第二個 }） */
function isPendingLineEmojiSuffixAtEnd(fragment, endsAtEof) {
  if (!endsAtEof) return false;
  const core = fragment.replace(/\s+$/u, '');
  return /^\{\{\$(\d+)$/.test(core) || /^\{\{\$(\d+)\}$/.test(core);
}

/** 誤刪 `{{` 其中一個 `{` 後變成 `{$n…`：結尾可補回 `{` 還原 */
function isPendingSingleOpenBraceEmojiSuffixAtEnd(fragment, endsAtEof) {
  if (!endsAtEof) return false;
  const core = fragment.replace(/\s+$/u, '');
  return (
    /^\{\$(\d+)$/.test(core) ||
    /^\{\$(\d+)\}$/.test(core) ||
    /^\{\$(\d+)\}\}$/.test(core)
  );
}

/**
 * 以 `{{$` 開頭但未形成合法 `{{$n}}` 的片段：若後面還有內容（非結尾可補完前綴）則整段刪除；
 * 若在全文結尾且為 `{{$n` 或 `{{$n}`（尚可能補上 `}`）則保留。
 * @returns {{ text: string, removed: Array<[number, number]> }} removed 為原文中的 [start, end) 區間
 */
export function stripIncompleteLineEmojiBrackets(text) {
  const input = text ?? '';
  const removed = [];
  let result = '';
  let i = 0;
  while (i < input.length) {
    if (input.slice(i, i + 3) === '{{$') {
      const rest = input.slice(i);
      const validMatch = rest.match(/^\{\{\$(\d+)\}\}/);
      if (validMatch) {
        result += validMatch[0];
        i += validMatch[0].length;
        continue;
      }
      let j = i + 3;
      while (j < input.length && /[0-9]/.test(input[j])) j++;
      while (j < input.length && input[j] === '}') j++;
      const fragment = input.slice(i, j);
      const atEnd = j === input.length;
      if (isPendingLineEmojiSuffixAtEnd(fragment, atEnd)) {
        result += fragment;
        i = j;
        continue;
      }
      removed.push([i, j]);
      i = j;
      continue;
    }
    // 誤刪 `{{` 其一：`{{$1}}` → `{$1}}`，不與上一段的 `{{$` 重疊（前一字元不可為 `{`）
    if (input.slice(i, i + 2) === '{$' && (i === 0 || input[i - 1] !== '{')) {
      let j = i + 2;
      while (j < input.length && /[0-9]/.test(input[j])) j++;
      while (j < input.length && input[j] === '}') j++;
      const fragment = input.slice(i, j);
      const atEnd = j === input.length;
      if (isPendingSingleOpenBraceEmojiSuffixAtEnd(fragment, atEnd)) {
        result += fragment;
        i = j;
        continue;
      }
      removed.push([i, j]);
      i = j;
      continue;
    }
    result += input[i];
    i++;
  }
  return { text: result, removed };
}

/**
 * 依序刪除原文中若干區間後，游標在「新字串」中的位置（caretOrig 為刪除前的 index）
 */
export function mapCaretAfterRemovingRanges(caretOrig, removed) {
  if (!removed.length) return caretOrig;
  const ranges = [...removed].sort((a, b) => a[0] - b[0]);
  let offset = 0;
  for (const [a, b] of ranges) {
    const len = b - a;
    if (caretOrig <= a) {
      return caretOrig - offset;
    }
    if (caretOrig >= b) {
      offset += len;
      continue;
    }
    return a - offset;
  }
  return caretOrig - offset;
}

/** LINE API 文字中表情佔位字元（含舊版編輯器 Private Use 佔位，僅供預覽相容） */
export function isLineEmojiPlaceholderChar(ch) {
  return ch === '$' || ch === '\uE700';
}

/**
 * 後端 / LINE 格式 → 編輯器文字 + { slot, productId, emojiId }[]
 * 依 index 由小到大對應 $1、$2…
 */
export function lineApiToEditor(text, emojis) {
  const t0 = text ?? '';
  if (!Array.isArray(emojis) || emojis.length === 0) {
    return { text: t0, rows: [] };
  }
  const asc = [...emojis]
    .filter((e) => typeof e?.index === 'number' && Number.isFinite(e.index))
    .sort((a, b) => a.index - b.index);
  let t = t0;
  for (let i = asc.length - 1; i >= 0; i--) {
    const e = asc[i];
    const slot = i + 1;
    const idx = e.index;
    if (idx >= 0 && idx < t.length) {
      const ch = t[idx];
      if (ch === '$' || ch === '\uE700') {
        t = t.slice(0, idx) + `{{$${slot}}}` + t.slice(idx + 1);
      }
    }
  }
  const rows = asc.map((e, i) => ({
    slot: i + 1,
    productId: String(e.productId || '').trim(),
    emojiId: String(e.emojiId ?? '')
  }));
  return { text: t, rows };
}

/**
 * 編輯器文字 + rows → LINE API 用 text、emojis
 * @returns {{ text: string, emojis: Array } | { error: string }}
 */
export function editorRowsToLineApi(editorText, rows) {
  const text = editorText ?? '';
  const raw = Array.isArray(rows) ? rows : [];
  const slotToRow = new Map();
  for (const r of raw) {
    const s = Number(r?.slot);
    if (!Number.isFinite(s) || s < 1) continue;
    slotToRow.set(s, r);
  }

  const matches = [];
  const re = new RegExp(LINE_EMOJI_BRACKET_RE.source, 'g');
  let m;
  while ((m = re.exec(text)) !== null) {
    matches.push({ slot: parseInt(m[1], 10), start: m.index, end: m.index + m[0].length });
  }

  const seenSlot = new Set();
  for (const mt of matches) {
    if (seenSlot.has(mt.slot)) {
      return { error: `佔位 {{$${mt.slot}}} 在訊息中重複，每個編號僅能出現一次。` };
    }
    seenSlot.add(mt.slot);
  }

  matches.sort((a, b) => a.start - b.start);

  let out = '';
  let last = 0;
  const lineEmojis = [];
  for (const mt of matches) {
    out += text.slice(last, mt.start);
    const row = slotToRow.get(mt.slot);
    if (!row || typeof row.productId !== 'string' || !row.productId.trim()) {
      return { error: `訊息中的 {{$${mt.slot}}} 在下方 emojis 設定中找不到對應資料（需有 slot、productId、emojiId）。` };
    }
    if (row.emojiId === undefined || row.emojiId === null || String(row.emojiId).trim() === '') {
      return { error: `{{$${mt.slot}}} 缺少 emojiId。` };
    }
    out += '$';
    lineEmojis.push({
      index: out.length - 1,
      productId: String(row.productId).trim(),
      emojiId: String(row.emojiId).padStart(3, '0')
    });
    last = mt.end;
  }
  out += text.slice(last);

  for (const s of slotToRow.keys()) {
    if (!seenSlot.has(s)) {
      return { error: `已設定 slot ${s} 的表情，但訊息內容缺少 {{$${s}}} 佔位。` };
    }
  }

  return { text: out, emojis: lineEmojis };
}

/** 預覽用：將 {{$n}} 換成圖（需 rows 對照） */
export function bracketPreviewSegments(text, rows) {
  const t = text ?? '';
  const slotToRow = new Map();
  if (Array.isArray(rows)) {
    for (const r of rows) {
      const s = Number(r?.slot);
      if (Number.isFinite(s) && s >= 1) slotToRow.set(s, r);
    }
  }
  const re = new RegExp(LINE_EMOJI_BRACKET_RE.source, 'g');
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(t)) !== null) {
    if (m.index > last) {
      out.push({ type: 'text', text: t.slice(last, m.index) });
    }
    const slot = parseInt(m[1], 10);
    const row = slotToRow.get(slot);
    if (row?.productId && row.emojiId != null && String(row.emojiId).trim() !== '') {
      out.push({
        type: 'emoji',
        productId: String(row.productId).trim(),
        emojiId: String(row.emojiId).padStart(3, '0')
      });
    } else {
      out.push({ type: 'text', text: m[0] });
    }
    last = m.index + m[0].length;
  }
  if (last < t.length) {
    out.push({ type: 'text', text: t.slice(last) });
  }
  return out.length ? out : [{ type: 'text', text: t }];
}
